import { useState } from "react";
import { Check, X, ShieldCheck, Loader2 } from "lucide-react";

const STATUS_STYLE = {
  present: "bg-emerald-50 text-emerald-700", absent: "bg-red-50 text-red-600",
  on_leave: "bg-amber-50 text-amber-700", half_day: "bg-amber-50 text-amber-700",
  holiday: "bg-slate-100 text-slate-600", weekly_off: "bg-slate-100 text-slate-600",
};

const KV = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className="text-right font-medium text-slate-800">{String(v)}</span>
  </div>
);

/** Renders a staged write action with Confirm / Cancel. Nothing is saved until Confirm. */
export function AiConfirmCard({ action, done, result, onResolve, disabled }) {
  const [busy, setBusy] = useState(false);
  const p = action.preview || {};

  const resolve = async (decision) => {
    setBusy(true);
    try { await onResolve(decision); } finally { setBusy(false); }
  };

  const label = { "attendance.mark": "Confirm & Save Attendance", "patient.create": "Confirm & Register",
    "opd.create": "Confirm OPD Registration", "appointment.create": "Confirm Appointment" }[action.type] || "Confirm";

  return (
    <div className="mt-3 max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid={`confirm-card-${action.type}`}>
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
        <ShieldCheck className="h-4 w-4 text-[#EA580C]" />
        <p className="text-[13px] font-semibold text-slate-800">{action.summary}</p>
      </div>
      <div className="px-4 py-3">
        {action.type === "attendance.mark" ? (
          <table className="w-full text-sm" data-testid="confirm-attendance-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="pb-1.5">Staff</th><th className="pb-1.5">Status</th><th className="pb-1.5">Reason</th>
              </tr>
            </thead>
            <tbody>
              {(p.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5 font-medium text-slate-800">{r.name}</td>
                  <td className="py-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-600"}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                    {r.existing_status && <span className="ml-1 text-[10px] text-amber-600">(was {r.existing_status})</span>}
                  </td>
                  <td className="py-1.5 text-slate-500">{r.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="divide-y divide-slate-100">
            {Object.entries(p).filter(([k, v]) => v && !Array.isArray(v)).map(([k, v]) => (
              <KV key={k} k={k.replace(/_/g, " ")} v={v} />
            ))}
            {Array.isArray(p.possible_name_matches) && p.possible_name_matches.length > 0 && (
              <p className="pt-2 text-[11px] text-amber-600">Similar names on record: {p.possible_name_matches.join(", ")}</p>
            )}
          </div>
        )}
      </div>
      {done ? (
        <div className={`border-t border-slate-100 px-4 py-2.5 text-[13px] font-medium ${result?.ok ? "text-emerald-700" : "text-slate-500"}`}
          data-testid="confirm-card-resolved">
          {result?.ok ? "✅ " : ""}{result?.message || "Resolved."}
        </div>
      ) : (
        <div className="flex gap-2 border-t border-slate-100 px-4 py-3">
          <button onClick={() => resolve("confirm")} disabled={busy || disabled} data-testid="confirm-action-btn"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#0F172A] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#1E293B] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {label}
          </button>
          <button onClick={() => resolve("cancel")} disabled={busy || disabled} data-testid="cancel-action-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
            <X className="h-4 w-4" /> Cancel
          </button>
        </div>
      )}
    </div>
  );
}
